import { useAxiosClient } from '../utils/useAxiosClient';
import { useQuery } from 'react-query';

export interface DeiIndexData {
  dei_index_chart: {
    displayDates: string[];
    dates: string[];
    brands: string[];
    values: number[][];
  };
  date_range: {
    start: string;
    end: string;
  };
}

export const useDeiIndex = (
  orgId: number,
  selectedPlatforms?: string[],
  selectedPillars?: string[],
  startDate?: string | null,
  endDate?: string | null,
  options?: { enabled?: boolean }
) => {
  const axiosClient = useAxiosClient();

  // Build query params
  const queryParams = new URLSearchParams();
  queryParams.append('org', orgId.toString());
  
  // Add platforms to query params
  if (selectedPlatforms && selectedPlatforms.length > 0) {
    selectedPlatforms.forEach(p => queryParams.append('platforms', p));
  }
  // Add pillars to query params
  if (selectedPillars && selectedPillars.length > 0) {
    selectedPillars.forEach(p => queryParams.append('pillars', p));
  }
  // Add date range to query params
  if (startDate) {
    queryParams.append('date_min', startDate);
  }
  if (endDate) {
    queryParams.append('date_max', endDate);
  }

  // Include platforms, pillars, and dates in the query key for proper cache invalidation
  const queryKey = ['deiIndex', orgId, selectedPlatforms, selectedPillars, startDate, endDate];

  const { data, isLoading, isFetching, error } = useQuery(
    queryKey,
    async () => {
      if (!orgId) {
        throw new Error('No organization selected');
      }

      // Make the API request with filters
      const response = await axiosClient.get<DeiIndexData>(`/api/get-dei-index?${queryParams.toString()}`);
      
      if ('error' in response.data) {
        throw new Error(typeof response.data.error === 'string' ? response.data.error : 'An error occurred');
      }
      
      return response.data;
    },
    {
      enabled: !!orgId && (options?.enabled !== false),
      staleTime: 0, // Data stale immediately → triggers background refetch on filter change
      cacheTime: 30 * 60 * 1000, // 30 minutes max cache lifetime
      refetchOnWindowFocus: false,
      retry: 1,
      keepPreviousData: true
    }
  );

  // Use isFetching instead of isLoading to show loading state during refetches
  // isLoading = true only when no cached data AND fetching
  // isFetching = true whenever fetching (including background refetch)
  return { 
    data: data ?? null,  // Convert undefined to null for component compatibility
    loading: isFetching, 
    error: error ? (error instanceof Error ? error.message : 'An error occurred') : null 
  };
}; 